import Box from '@mui/material/Box'
import { SxProps } from '@mui/material/styles'
import { ReactNode } from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { NumberFormatValues } from 'react-number-format'
import ErrorText from './ErrorText'
import FieldLabel from './FieldLabel'
import HelpText from './HelpText'
import NumericFormatField from './NumericFormatField'

const textFieldStyle: SxProps = {
  '& .MuiTextField-root': {
    width: '100%',
  },
}

type NumberFieldProps<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  label?: ReactNode
  footer?: ReactNode
  help?: ReactNode
  allowNegative?: boolean
  decimalScale?: number
  startAdornment?: ReactNode
  endAdornment?: ReactNode
  value?: string | number
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  isAllowed?: (values: NumberFormatValues) => boolean
  width?: string | number
  placeholder?: string
  labelStyle?: SxProps
  fullWidth?: boolean
  disabled?: boolean
  required?: boolean
  allowLeadingZeros?: boolean
  showError?: boolean
}

const NumberField = <T extends FieldValues>({
  name,
  control,
  label,
  help,
  allowNegative = false,
  decimalScale = 0,
  startAdornment,
  endAdornment,
  value,
  onChange,
  isAllowed,
  width,
  placeholder,
  labelStyle,
  fullWidth = false,
  disabled = false,
  required = true,
  allowLeadingZeros = false,
  showError = true,
}: NumberFieldProps<T>) => {
  return (
    <Box
      sx={{
        width: fullWidth ? '100%' : undefined,
      }}>
      <Controller
        name={name}
        control={control}
        render={({
          field: { onChange: onControllerChange, value: controllerValue },
          fieldState: { error },
        }) => (
          <>
            {!!label && (
              <FieldLabel labelStyle={labelStyle} required={required}>
                {label}
              </FieldLabel>
            )}
            <HelpText>{help}</HelpText>
            <Box
              sx={[
                {
                  width: width,
                },
                ...(Array.isArray(textFieldStyle) ? textFieldStyle : [textFieldStyle]),
              ]}>
              <NumericFormatField
                isAllowed={isAllowed}
                placeholder={placeholder}
                allowNegative={allowNegative}
                decimalScale={decimalScale}
                startAdornment={startAdornment}
                endAdornment={endAdornment}
                error={!!error}
                onChange={!!onChange ? onChange : onControllerChange}
                value={value ?? controllerValue ?? ''}
                disabled={disabled}
                allowLeadingZeros={allowLeadingZeros}
              />
            </Box>
            {showError && error && <ErrorText>{error.message}</ErrorText>}
          </>
        )}
      />
    </Box>
  )
}

export default NumberField
